import React, { useState, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { API, fmtDate, printArea } from '../finance/shared';
import { effectiveStatus, subscriptionBadge } from './subscriptionUtils';
import Icon from '../../../components/dashboard/DashboardIcons';

const CategoryPrintStyle = () => (
  <style>{`
    @page { size: A4; margin: 12mm; }
    @media print {
      #root { display: none !important; }
      body * { visibility: hidden !important; }
      .cat-print-area, .cat-print-area * { visibility: visible !important; }
      .cat-print-root { position: static !important; display: block !important; background: none !important; padding: 0 !important; }
      .cat-print-inner { position: static !important; width: 100% !important; max-width: none !important; }
      .cat-print-scrollwrap { position: static !important; max-height: none !important; overflow: visible !important; box-shadow: none !important; }
      .cat-print-area { position: static !important; box-shadow: none !important; margin: 0 !important; }
      .cat-print-area table { width: 100%; border-collapse: collapse; }
      .cat-print-area thead { display: table-header-group; }
      .cat-print-area tr { break-inside: avoid; page-break-inside: avoid; }
    }
  `}</style>
);

const FILTERS = [
  { key: 'all', label: 'الكل' },
  { key: 'active', label: 'اشتراك فعّال' },
  { key: 'expired', label: 'منتهي الاشتراك' },
  { key: 'none', label: 'بدون اشتراك' },
];

const matchesFilter = (p, filterKey) => {
  if (filterKey === 'all') return true;
  const status = effectiveStatus(p);
  if (filterKey === 'active') return status === 'active';
  if (filterKey === 'expired') return status === 'expired';
  if (filterKey === 'none') return status === 'not_registered';
  return true;
};

export default function CategoryPlayersModal({ category, players, club, onClose }) {
  const [filter, setFilter] = useState('all');
  const [printMode, setPrintMode] = useState(false);

  const categoryPlayers = useMemo(() => players.filter((p) => p.category_id === category.id), [players, category.id]);
  const visible = useMemo(
    () => categoryPlayers.filter((p) => matchesFilter(p, filter)).sort((a, b) => (a.name || '').localeCompare(b.name || '', 'ar')),
    [categoryPlayers, filter]
  );

  const filterLabel = FILTERS.find((f) => f.key === filter)?.label || 'الكل';
  const logoUrl = club?.logo_file_id ? `${API}/files/${club.logo_file_id}` : null;

  if (printMode) {
    return ReactDOM.createPortal(
      <div className="cat-print-root fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4">
        <CategoryPrintStyle />
        <div className="absolute inset-0 print:hidden" onClick={() => setPrintMode(false)} />
        <div className="cat-print-inner relative w-full max-w-2xl">
          <div className="cat-print-scrollwrap max-h-[85vh] overflow-y-auto rounded-xl shadow-2xl">
            <div className="cat-print-area bg-white p-6" data-testid="category-players-print-view">
              <div className="mb-4 flex items-center justify-between border-b border-slate-200 pb-3">
                <div className="flex items-center gap-3">
                {logoUrl && <img src={logoUrl} alt="" className="h-12 w-12 rounded-lg object-contain" onError={(e) => { e.target.style.display = 'none'; }} />}
                <div>
                  <div className="text-base font-bold text-slate-900">{club?.name || 'المنشأة'}</div>
                  <div className="text-xs text-slate-500">قائمة لاعبي فئة «{category.name}» — {filterLabel}</div>
                </div>
              </div>
              <div className="text-left text-xs text-slate-500">
                <div>تاريخ الطباعة: {fmtDate(new Date())}</div>
                <div>العدد: {visible.length} لاعب</div>
              </div>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-300 text-right text-xs text-slate-600">
                  <th className="py-1.5 w-10">م</th>
                  <th className="py-1.5">اسم اللاعب</th>
                  <th className="py-1.5">رقم اللاعب</th>
                  <th className="py-1.5">حالة الاشتراك</th>
                  <th className="py-1.5">تاريخ الانتهاء</th>
                  <th className="py-1.5 w-24">حضور / توقيع</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((p, i) => (
                  <tr key={p.id} className="border-b border-slate-100">
                    <td className="py-2">{i + 1}</td>
                    <td className="py-2 font-semibold">{p.name}</td>
                    <td className="py-2" dir="ltr">{p.jersey_number || '—'}</td>
                    <td className="py-2">{subscriptionBadge(p).label}</td>
                    <td className="py-2">{p.subscription_end ? fmtDate(p.subscription_end) : '—'}</td>
                    <td className="py-2"></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {visible.length === 0 && <div className="py-8 text-center text-sm text-slate-400">لا يوجد لاعبون مطابقون لهذا الفلتر</div>}
            </div>
          </div>
          <div className="mt-3 flex justify-center gap-2 print:hidden">
            <button onClick={printArea} className="flex items-center gap-2 rounded-lg bg-[#16233b] px-4 py-2 text-sm font-semibold text-white hover:bg-[#1e2f4e]" data-testid="category-players-do-print">
              <Icon name="document" className="w-4 h-4" /> طباعة
            </button>
            <button onClick={() => setPrintMode(false)} className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm text-slate-600" data-testid="category-players-print-close">رجوع للقائمة</button>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-0 sm:items-center sm:p-4" data-testid="category-players-modal">
      <div className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-t-2xl bg-white shadow-2xl sm:rounded-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <div>
            <h3 className="text-base font-bold text-slate-900" data-testid="category-players-modal-title">لاعبو فئة «{category.name}»</h3>
            <p className="text-xs text-slate-500" data-testid="category-players-result-count">{visible.length} من {categoryPlayers.length} لاعب</p>
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100" data-testid="category-players-close-btn"><Icon name="x" className="w-5 h-5" /></button>
        </div>

        <div className="flex items-center gap-2 overflow-x-auto border-b border-slate-100 px-5 py-3">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${filter === f.key ? 'bg-[#16233b] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              data-testid={`category-players-filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-3">
          {visible.length === 0 ? (
            <div className="flex flex-col items-center py-10 text-center" data-testid="category-players-empty">
              <span className="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-400"><Icon name="userGroup" className="w-5 h-5" /></span>
              <p className="text-sm text-slate-500">لا يوجد لاعبون مطابقون لهذا الفلتر</p>
            </div>
          ) : (
            <div className="space-y-2">
              {visible.map((p) => {
                const badge = subscriptionBadge(p);
                return (
                  <div key={p.id} className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50/50 px-3 py-2.5" data-testid="category-players-row">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-bold text-slate-800">{p.name}</div>
                      <div className="text-xs text-slate-400">{p.jersey_number ? `رقم ${p.jersey_number}` : ''} {p.subscription_end ? `· ينتهي ${fmtDate(p.subscription_end)}` : ''}</div>
                    </div>
                    <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-bold ${badge.cls}`}>{badge.label}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-100 px-5 py-3">
          <button
            onClick={() => setPrintMode(true)}
            disabled={visible.length === 0}
            className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:border-teal-300 disabled:cursor-not-allowed disabled:opacity-40"
            data-testid="category-players-print-btn"
          >
            <Icon name="document" className="w-4 h-4" /> طباعة القائمة
          </button>
        </div>
      </div>
    </div>
  );
}
